import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDefaultDateRange } from '../../../../lib/utils';
import {
  getCurrentProject,
  getCurrentUserAssociation,
} from '../../../../modules/project';
import Loader from '../../../elements/Loader';
import DashboardFilters from './metrics/DashboardFilters';
import ExportButtonAndFilters from './ExportButtonAndFilters';
import DashboardSections from './DashboardSections';

const DashboardTabContent = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const sectionRefs = useRef({});
  const pageRef = useRef();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isJiraIntegrated, setIsJiraIntegrated] = useState(false);
  const [linkedJiraProjects, setLinkedJiraProjects] = useState([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState(null);

  const [dateFilters, setDateFilters] = useState({
    ...getDefaultDateRange(),
    defaultRange: true,
  });

  const params = new URLSearchParams(location.search);
  const currentView = params.get('view');

  useEffect(() => {
    const fetchUserAssociation = async () => {
      try {
        const response = await getCurrentUserAssociation(projectId);
        if (
          response?.role === 'admin' ||
          response?.role === 'org-admin' ||
          response?.role === 'org-manager' ||
          response?.role === 'executive-manager'
        ) {
          setIsAdmin(true);
        }
      } catch (error) {
        if (error.message === 'You do not have access to this project') {
          navigate('/access-denied');
        }
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAssociation();
  }, [projectId, navigate]);

  const { data: currentProject, isLoading: projectLoading } = useQuery({
    queryKey: ['currentProject', projectId],
    queryFn: () => getCurrentProject(projectId),
    enabled: !!projectId,
  });

  useEffect(() => {
    if (!currentProject) return;

    const jiraProjects = currentProject.linked_jira_projects || [];

    setLinkedJiraProjects(jiraProjects);
    setSelectedJiraProject(jiraProjects[0] || null);
    setIsJiraIntegrated(
      currentProject.integrated_platform === 'jira' &&
      jiraProjects.length > 0
    );
  }, [currentProject]);

  const handleApplyFilters = (filters) => {
    setDateFilters(filters);
  };

  const handleJiraProjectChange = (project) => {
    setSelectedJiraProject(project);
  };

  if (loading || projectLoading) {
    return <Loader />;
  }

  return (
    <Container fluid className="py-2 px-0">
      <div className={currentView ? 'sticky-top-bar' : ''}>
        <ExportButtonAndFilters
          exporting={exporting}
          setExporting={setExporting}
          currentProject={currentProject}
          selectedJiraProject={selectedJiraProject}
          linkedJiraProjects={linkedJiraProjects}
          isJiraIntegrated={isJiraIntegrated}
          currentView={currentView}
          setIsFilterOpen={setIsFilterOpen}
          handleJiraProjectChange={handleJiraProjectChange}
          pageRef={pageRef}
          sectionRefs={sectionRefs}
        />

        <DashboardFilters
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          dateFilters={dateFilters}
          linkedJiraProjects={linkedJiraProjects}
        />
      </div>
      <div ref={pageRef} className="dashboard-report-section">
        <DashboardSections
          currentView={currentView}
          projectId={projectId}
          isAdmin={isAdmin}
          dateFilters={dateFilters}
          isJiraIntegrated={isJiraIntegrated}
          selectedJiraProject={selectedJiraProject}
          sectionRefs={sectionRefs}
        />
      </div>
    </Container>
  );
};

export default DashboardTabContent;
